import {useState, useEffect} from 'react';
import DOMUtils from '@spothero/utils/dom';

/**
 * Spot hover behavior is seen across the Transient/Monthly/Event Packages UI,
 * on the spot cards and spot map.
 *
 * The custom DOM events and listeners allow spot cards, map markers,
 * and potentially other element that may represent a spot to coordinate
 * highlighting/unhighlighting when any given one of those elements is hovered over.
 *
 * SpotIds used across the app have inconsistent type, sometimes appearing as
 * a string and sometimes a number. Functions in this file expect to be passed
 * string spotIds, but parse them into integers for the sake of backwards compatibility
 * with older parts of the UI.
 */

/**
 * Use to set up onMouseEnter events or equivalent
 *
 * @param {string} spotId - unique spot id from database
 * @returns {Function} that sends a custom DOM event indicating this spot is
 * being hovered over
 */
export const onMouseEnter = spotId => () => {
    DOMUtils.sendCustomEvent('spotHover', {
        hovered: true,
        spotId: parseInt(spotId, 10),
    });
};

/**
 * Use to set up onMouseLeave events or equivalent
 *
 * @param {string} spotId - unique spot id from database
 * @returns {Function} that sends a custom DOM event indicating this spot is
 * no longer being hovered over
 */
export const onMouseLeave = spotId => () => {
    DOMUtils.sendCustomEvent('spotHover', {
        hovered: false,
        spotId: parseInt(spotId, 10),
    });
};

/**
 * React Hook for listening to the custom spotHover events and determining
 * whether the component implementing this should coordinate hover/highlight
 * state
 *
 * @param {string} spotId - unique spot id from database
 * @returns {boolean} whether the implementing component is considered being hovered over
 */
const useSpotHover = spotId => {
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        const onSpotHover = evt => {
            const {
                detail: {hovered, spotId: incomingSpotId},
            } = evt;
            const incomingSpotIdAsString = `${incomingSpotId}`;

            if (incomingSpotIdAsString === spotId) {
                setIsHovered(hovered);
            }
        };

        document.addEventListener('spotHover', onSpotHover, {capture: true});

        // Clean up event listeners after this effect:
        return () => {
            document.removeEventListener('spotHover', onSpotHover);
        };
    }, [spotId]);

    return isHovered;
};

export default useSpotHover;
